'use client';

import { Button } from '@instamenu/ui-system-next';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from 'react-responsive';
import { Phone, Store } from 'lucide-react';
import { formatAddress } from '@instamenu/formatters';
import type { Order } from '@instamenu/context-checkout';

import { CompleteLoyalty } from '../loyalty-status/complete-loyalty';
import { EnrollmentCard3D } from '../loyalty-status/enrollment-card';
import { makeCompletionMessage } from './confirmation-message';
import {
  ActionRow,
  InfoCard,
  MapPreviewCard,
  OrderCompleteHero,
  OrderCompleteLayout,
  OrderCompleteStack,
  OrderCompleteTwoColumn,
  StatusPill,
} from './components';
import { getPickupPillContent } from './status-pill-content';

type PickupOrderProps = {
  order: Order;
  userToRestaurantDistance?: number;
};

export const PickupOrder = ({ order, userToRestaurantDistance }: PickupOrderProps) => {
  const router = useRouter();
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });
  const showMap =
    !!order.restaurant.address?.location &&
    (userToRestaurantDistance === undefined || userToRestaurantDistance > 50);
  const description = makeCompletionMessage(order);
  const greeting = `Thanks${order.user?.firstName ? `, ${order.user.firstName}` : ''}!`;

  const pillContent = getPickupPillContent(order);
  const readyPill = (
    <StatusPill
      tone={pillContent.tone}
      icon={pillContent.icon}
      data-id="order-complete-status-pill"
    >
      {pillContent.label}
    </StatusPill>
  );

  const restaurantInfo = (
    <InfoCard
      icon={<Store size={18} strokeWidth={2} />}
      title={order.restaurant.name}
      subtitle={formatAddress(order.restaurant.address)}
      data-id="order-complete-restaurant-card"
    />
  );

  const map = showMap ? <MapPreviewCard mappable={order.restaurant} tall={isDesktop} /> : null;

  const actions = (
    <ActionRow>
      <Button
        css={{ flex: 2 }}
        onClick={() => router.push(`${process.env.NEXT_PUBLIC_API_ENDPOINT}/orders/${order.id}`)}
        data-id="order-complete-view-receipt"
      >
        View Receipt
      </Button>
      {order.restaurant.phone ? (
        <Button
          variant="secondary"
          css={{ flex: 1 }}
          onClick={() => router.push(`tel:${order.restaurant.phone}`)}
          data-id="order-complete-call"
        >
          <Phone size={16} strokeWidth={2} style={{ marginRight: 6 }} />
          Call
        </Button>
      ) : null}
    </ActionRow>
  );

  const loyalty3D = (
    <>
      <CompleteLoyalty />
      <EnrollmentCard3D />
    </>
  );

  // The full composed pickup description ("Your order will be ready today in
  // about N minutes. We'll send you a text when your order is ready." — or for
  // scheduled orders, the formatted day/time) is rendered inside the hero on
  // every viewport so the wait-time/scheduled-time data is never dropped on
  // mobile, and so we don't need a separate redundant SMS notice card.
  const hero = <OrderCompleteHero pill={readyPill} greeting={greeting} description={description} />;

  return (
    <OrderCompleteLayout hero={hero}>
      {isDesktop && map ? (
        <OrderCompleteTwoColumn
          left={map}
          right={
            <OrderCompleteStack>
              {restaurantInfo}
              {actions}
              {loyalty3D}
            </OrderCompleteStack>
          }
        />
      ) : (
        <OrderCompleteStack>
          {restaurantInfo}
          {map}
          {actions}
          {loyalty3D}
        </OrderCompleteStack>
      )}
    </OrderCompleteLayout>
  );
};
